import React, { useState } from 'react';
import DOMPurify from 'dompurify';
import { useTranslation } from 'react-i18next';
import type {
  InboundEmail,
  ContactFormSubmission,
  EmailStatus,
  EmailPriority,
} from '../../services/emailHubService';
import IntentBadge from './IntentBadge';
import ReplyComposer from './ReplyComposer';

type DetailItem = InboundEmail | ContactFormSubmission;

function isInboundEmail(item: DetailItem): item is InboundEmail {
  return 'fromAddress' in item;
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleString();
}

interface EmailDetailProps {
  item: DetailItem;
  onReply: (body: string) => Promise<void>;
  onUpdateStatus: (status: EmailStatus) => Promise<void>;
  onUpdatePriority: (priority: EmailPriority) => Promise<void>;
  onUpdateNotes?: (notes: string) => Promise<void>;
  onDelete?: () => Promise<void>;
  onClose?: () => void;
  replying?: boolean;
}

const STATUS_OPTIONS: EmailStatus[] = ['new', 'read', 'replied', 'auto_replied', 'archived', 'spam'];
const PRIORITY_OPTIONS: EmailPriority[] = ['urgent', 'high', 'normal', 'low'];

const EmailDetail: React.FC<EmailDetailProps> = ({
  item,
  onReply,
  onUpdateStatus,
  onUpdatePriority,
  onUpdateNotes,
  onDelete,
  onClose,
  replying,
}) => {
  const { t } = useTranslation('emailHub');
  const [notes, setNotes] = useState(
    isInboundEmail(item) ? (item.notes ?? '') : '',
  );
  const [savingNotes, setSavingNotes] = useState(false);
  const [notesSaved, setNotesSaved] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const email = isInboundEmail(item) ? item : null;
  const form = !isInboundEmail(item) ? (item as ContactFormSubmission) : null;

  const senderEmail = email ? email.fromAddress : form!.email;
  const senderName = email ? email.fromName : form!.name;
  const subject = email ? email.subject : (form!.subject ?? t('detail.noSubject', '(No subject)'));
  const htmlBody = email ? email.htmlBody : null;
  const textBody = email ? email.textBody : form!.message;
  const matchedUser = email?.matchedUser ?? form?.matchedUser ?? null;

  const handleSaveNotes = async () => {
    if (!onUpdateNotes) return;
    setSavingNotes(true);
    try {
      await onUpdateNotes(notes);
      setNotesSaved(true);
      setTimeout(() => setNotesSaved(false), 2000);
    } finally {
      setSavingNotes(false);
    }
  };

  return (
    <div className="email-hub-detail">
      {/* Scrollable content area */}
      <div className="email-hub-detail__content">
      {/* Header */}
      <div className="email-hub-detail__header">
        <div className="email-hub-detail__header-top">
          <h2 className="email-hub-detail__subject">{subject}</h2>
          <div className="email-hub-detail__header-actions">
            {onDelete && (
              <button
                className="email-hub-detail__delete"
                onClick={() => setShowDeleteConfirm(true)}
                aria-label={t('detail.delete', 'Delete')}
                title={t('detail.delete', 'Delete')}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                  <line x1="10" y1="11" x2="10" y2="17" />
                  <line x1="14" y1="11" x2="14" y2="17" />
                </svg>
              </button>
            )}
            {onClose && (
              <button
                className="email-hub-detail__close"
                onClick={onClose}
                aria-label={t('detail.close', 'Close')}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Meta row */}
        <div className="email-hub-detail__meta">
          <div className="email-hub-detail__sender-block">
            <div className="email-hub-detail__sender-avatar">
              {(senderName || senderEmail).charAt(0).toUpperCase()}
            </div>
            <div>
              {senderName && (
                <div className="email-hub-detail__sender-name">{senderName}</div>
              )}
              <div className="email-hub-detail__sender-email">{senderEmail}</div>
            </div>
          </div>
          <div className="email-hub-detail__date">{formatDate(item.createdAt)}</div>
        </div>

        {/* Badges */}
        <div className="email-hub-detail__badges">
          {item.intent && (
            <IntentBadge intent={item.intent} confidence={item.intentConfidence} />
          )}
          <span className={`email-hub-status-badge email-hub-status-badge--${item.status}`}>
            {t(`status.${item.status}`, item.status)}
          </span>
          {email && (
            <span className={`email-hub-priority-badge email-hub-priority-badge--${email.priority}`}>
              {t(`priority.${email.priority}`, email.priority)}
            </span>
          )}
        </div>
      </div>

      {/* Controls row */}
      <div className="email-hub-detail__controls">
        <div className="email-hub-detail__control-group">
          <label className="email-hub-detail__control-label">
            {t('detail.status', 'Status')}
          </label>
          <select
            className="email-hub-detail__select"
            value={item.status}
            onChange={(e) => onUpdateStatus(e.target.value as EmailStatus)}
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>{t(`status.${s}`, s)}</option>
            ))}
          </select>
        </div>
        {email && (
          <div className="email-hub-detail__control-group">
            <label className="email-hub-detail__control-label">
              {t('detail.priority', 'Priority')}
            </label>
            <select
              className="email-hub-detail__select"
              value={email.priority}
              onChange={(e) => onUpdatePriority(e.target.value as EmailPriority)}
            >
              {PRIORITY_OPTIONS.map((p) => (
                <option key={p} value={p}>{t(`priority.${p}`, p)}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Matched user */}
      {matchedUser && (
        <div className="email-hub-detail__matched-user">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
            <circle cx="12" cy="7" r="4"/>
          </svg>
          <span>{t('detail.matchedUser', 'Matched user')}:</span>
          <strong>{matchedUser.fullName || matchedUser.username}</strong>
          <span className="email-hub-detail__matched-email">{matchedUser.email}</span>
        </div>
      )}

      {/* Body */}
      <div className="email-hub-detail__body-wrap">
        {htmlBody ? (
          <div
            className="email-hub-detail__html-body"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(htmlBody) }}
          />
        ) : (
          <pre className="email-hub-detail__text-body">{textBody}</pre>
        )}
      </div>

      {/* Replies history */}
      {item.replies && item.replies.length > 0 && (
        <div className="email-hub-detail__replies">
          <h3 className="email-hub-detail__replies-title">
            {t('detail.replies', 'Replies')} ({item.replies.length})
          </h3>
          {item.replies.map((reply, idx) => (
            <div key={idx} className="email-hub-detail__reply-item">
              <div className="email-hub-detail__reply-meta">
                <span className="email-hub-detail__reply-sender">Loopy AI Support</span>
                <span className="email-hub-detail__reply-time">{formatDate(reply.sentAt)}</span>
              </div>
              <pre className="email-hub-detail__reply-body">{reply.body}</pre>
            </div>
          ))}
        </div>
      )}

      {/* Internal notes */}
      {onUpdateNotes && (
        <div className="email-hub-detail__notes">
          <label className="email-hub-detail__notes-label">
            {t('detail.internalNotes', 'Internal Notes')}
          </label>
          <textarea
            className="email-hub-detail__notes-textarea"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={t('detail.notesPlaceholder', 'Add internal notes visible only to admins...')}
            rows={3}
          />
          <button
            className="email-hub-detail__notes-save"
            onClick={handleSaveNotes}
            disabled={savingNotes}
          >
            {savingNotes
              ? t('common:saving', 'Saving...')
              : notesSaved
                ? t('common:saved', 'Saved')
                : t('detail.saveNotes', 'Save Notes')}
          </button>
        </div>
      )}

      </div>{/* end scrollable content area */}

      {/* Fixed bottom reply composer */}
      <div className="email-hub-detail__composer">
        <ReplyComposer
          onSend={onReply}
          loading={replying}
          placeholder={t('reply.placeholder', 'Type your reply to {{email}}...', { email: senderEmail })}
        />
      </div>

      {/* Delete confirmation modal */}
      {showDeleteConfirm && (
        <div className="email-hub-modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="email-hub-modal" onClick={(e) => e.stopPropagation()}>
            <div className="email-hub-modal__icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                <line x1="10" y1="11" x2="10" y2="17" />
                <line x1="14" y1="11" x2="14" y2="17" />
              </svg>
            </div>
            <h3 className="email-hub-modal__title">
              {t('detail.deleteConfirmTitle', 'Delete this message?')}
            </h3>
            <p className="email-hub-modal__message">
              {t('detail.deleteConfirmMessage', 'This action cannot be undone. The message and all replies will be permanently deleted.')}
            </p>
            <div className="email-hub-modal__actions">
              <button
                className="email-hub-modal__btn email-hub-modal__btn--cancel"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
              >
                {t('common:cancel', 'Cancel')}
              </button>
              <button
                className="email-hub-modal__btn email-hub-modal__btn--delete"
                disabled={deleting}
                onClick={async () => {
                  if (!onDelete) return;
                  setDeleting(true);
                  try {
                    await onDelete();
                    setShowDeleteConfirm(false);
                  } finally {
                    setDeleting(false);
                  }
                }}
              >
                {deleting
                  ? t('detail.deleting', 'Deleting...')
                  : t('detail.confirmDelete', 'Delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailDetail;
